import { apiSlice } from "./apiSlice";
import { MapUser } from "../mappers/User";

// Common function to categorize users
const categorizeUsers = (responseData: any) => {
    const mapped: any = {
        pendingIso: [],
        pendingNida: [],
        pendingUser: [],
        pendingTraining: [],
        active: [],
        inactive: [],
        rejected: [],
        allUsers: []
    };

    responseData.forEach((beUser: any) => {
        const user = MapUser(beUser);

        mapped.allUsers.push(user)
        switch (user.ducStatus) {
        case "Approved":
            if(user?.signingUrl === "Pending ISO approval"){
                mapped.pendingIso.push(user);
            } else if(user?.signingUrl === "Pending NIDA approval"){
                mapped.pendingNida.push(user);
            } else if(user?.signingUrl === "DAC declined to sign" && user?.ducType === "AIAN"){//in case of AIAN status will be TrainingComplete (because of General) and AIAN declined
                mapped.rejected.push(user);
            } else if(user?.signingUrl === "NotSubmitted" && user?.ducType === "AIAN"){//in case of AIAN status will be TrainingComplete (because of General) and AIAN declined
                mapped.inactive.push(user);
            } else if(user.dateSignedCommittee){
                mapped.pendingTraining.push(user);
            }
            break;

        case "TrainingExpired":
            mapped.inactive.push(user);
            break;
        case "Expired":
        case "DucExpired":
            if(user?.signingUrl === "Pending ISO approval"){
                mapped.pendingIso.push(user);
            } else if(user?.signingUrl === "Pending NIDA approval") {
                mapped.pendingNida.push(user);
            }else if(user?.signingUrl === "DUC Approved"){
                mapped.pendingTraining.push(user);
            }
            else{
                mapped.inactive.push(user);
            }
            break;
        case "Declined":
            if (user.declined_by === "EnvelopeExpiry") {
                mapped.inactive.push(user);
            } else {
                mapped.rejected.push(user);
            }
            break;
        case "TrainingComplete":
            if(user?.signingUrl === "Pending ISO approval"){
                mapped.pendingIso.push(user);
            } else if(user?.signingUrl === "Pending NIDA approval"){
                mapped.pendingNida.push(user);
            } else if(user?.signingUrl === "DAC declined to sign" && user?.ducType === "AIAN"){//in case of AIAN status will be TrainingComplete (because of General) and AIAN declined
                mapped.rejected.push(user);
            } else if(user?.signingUrl === "NotSubmitted" && user?.ducType === "AIAN"){//in case of AIAN status will be TrainingComplete (because of General) and AIAN declined
                mapped.inactive.push(user);
            }else {
                mapped.active.push(user);
            }
            break;
        case "Revoked":
            mapped.rejected.push(user);
            break;
        default:
            if(user?.signingUrl === "Pending ISO approval"){
                mapped.pendingIso.push(user);
            }
            else if (user?.signingUrl === "Pending NIDA approval"){
                mapped.pendingNida.push(user);
            }
            else{
                mapped.pendingUser.push(user)
            }
            break;
        }
    });

    return mapped;
};
export const userApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({

        //TODO  All calls here are reactive and should be handled as stream baseStreamQuery.
        //TODO  Endpoints added as list of what we currently support on the back end.

        userInfo: builder.query({
            query: () => 'public/users/user_info',
            transformResponse: (responseData: any) => {
                return MapUser(responseData);
            }
        }),

        getUser: builder.query({
            query: ({ username }) => `/admin/users/username/${username}`,
            transformResponse: (responseData: any) => {
                return MapUser(responseData);
            }
        }),

        revokeUser: builder.mutation({
            query: ({ username, revokeReason }) => ({
                url: `/admin/users/revoke/${username}`,
                method: 'POST',
                // LC-2126: reason selected by the DAC admin; persisted and included in the revocation email
                body: { revokeReason }
            }),
            transformResponse: (responseData: any) => MapUser(responseData),
            invalidatesTags: ['All_Users']
        }),

        searchUsername: builder.query({
            query: ({ username }) => `/admin/users/search/username/${username}`,
            transformResponse: (responseData: any) => categorizeUsers(responseData)
        }),

        searchEmail: builder.query({
            query: ({ email }) => `/admin/users/search/email/${email}`,
            transformResponse: (responseData: any) => categorizeUsers(responseData)
        }),

        searchFirstName: builder.query({
            query: ({ firstName }) => `/admin/users/search/first-name/${firstName}`,
            transformResponse: (responseData: any) => categorizeUsers(responseData)
        }),

        searchLastName: builder.query({
            query: ({ lastName }) => `/admin/users/search/last-name/${lastName}`,
            transformResponse: (responseData: any) => categorizeUsers(responseData)
        }),

        getAll: builder.query({
            query: () => '/admin/users',
            providesTags: ['All_Users'],
            transformResponse: (responseData: any) => categorizeUsers(responseData)
        }),
        getAllByDucType: builder.query({
            query: ({ ducType }) => `/admin/users/ducType/${ducType}`,
            providesTags: ['All_Users'],
            transformResponse: (responseData: any) => categorizeUsers(responseData)
        }),

        updateUser: builder.mutation({
            query: (updatedFields) => {
                return {
                    url: `/users/${updatedFields.username}`,
                    method: 'PATCH',
                    body:  updatedFields
                }
            },
            invalidatesTags: ['All_Users']
        }),

        getUrl: builder.query({
            // Scope by ducType when known: a PI can have multiple InProgress DUCs at once
            // (e.g. an AIAN application and a General renewal), and without the ducType the
            // backend may resolve the signing URL for the wrong document.
            query: ({ email, ducType }) => ducType
                ? `/admin/url/email/${email}/ducType/${ducType}`
                : `/admin/url/email/${email}`
        }),

        getPendingOwnershipTransfers: builder.query({
            query: () => '/admin/duc/ownership-transfer/pending',
            providesTags: ['All_Users'],
            // LassoGrid expects an `id` field (idKey='id' is shared across every admin dashboard tab)
            transformResponse: (responseData: any) => (responseData ?? []).map((row: any) => ({ ...row, id: row.transferId })),
        }),

    })
});

export const {
    useUserInfoQuery,
    useGetAllQuery,
    useGetUserQuery,
    useRevokeUserMutation,
    useSearchUsernameQuery,
    useSearchEmailQuery,
    useLazySearchEmailQuery,
    useSearchFirstNameQuery,
    useSearchLastNameQuery,
    useUpdateUserMutation,
    useGetUrlQuery,
    useLazyGetUrlQuery,
    useGetAllByDucTypeQuery,
    useGetPendingOwnershipTransfersQuery
} = userApiSlice;
